import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  MessageSquare,
  Building2,
  LogOut,
  Network,
  Shield,
  Globe,
  Activity,
  Search,
} from 'lucide-react';
import { cognitoAuth } from '@/services/auth/cognito';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Super Admin only navigation - Platform management
const superAdminNavItems: NavItem[] = [
  {
    title: 'Tenants',
    href: '/tenants',
    icon: Building2,
  },
  {
    title: 'All Users',
    href: '/all-users',
    icon: Users,
  },
  {
    title: 'Platform Settings',
    href: '/global-settings',
    icon: Globe,
  },
  {
    title: 'System Health',
    href: '/system-health',
    icon: Activity,
  },
];

// Tenant Admin navigation - Organization management
const tenantAdminNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
  },
  {
    title: 'Organization',
    href: '/organization',
    icon: Network,
  },
  {
    title: 'Users',
    href: '/users',
    icon: Users,
  },
];

// Content management - for tenant users
const contentNavItems: NavItem[] = [
  {
    title: 'Knowledge Base',
    href: '/knowledge-base',
    icon: BookOpen,
  },
  {
    title: 'Chat inside Knowledge Base',
    href: '/kb-chat',
    icon: MessageSquare,
  },
  {
    title: 'Image Search',
    href: '/image-search',
    icon: Search,
  },
];


function NavMain({ items, label }: { items: NavItem[]; label?: string }) {
  const location = useLocation();

  return (
    <SidebarGroup className="px-2 py-0">
      {label && (
        <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-wider">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              asChild
              isActive={
                item.href === '/'
                  ? location.pathname === '/'
                  : location.pathname === item.href ||
                    location.pathname.startsWith(item.href + '/')
              }
              tooltip={{ children: item.title }}
            >
              <Link to={item.href}>
                <item.icon className="h-4 w-4" />
                <span>{item.title}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavUser() {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuthStore();

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    cognitoAuth.signOut();
    logout();
    navigate('/login');
  };

  const getRoleBadge = () => {
    if (isSuperAdmin()) {
      return (
        <Badge variant="destructive" className="text-[10px] px-1 py-0">
          Super Admin
        </Badge>
      );
    }
    if (user?.role === 'tenant_admin') {
      return (
        <Badge variant="secondary" className="text-[10px] px-1 py-0">
          Admin
        </Badge>
      );
    }
    return null;
  };

  return (
    <SidebarMenu>
      {/* User Info */}
      <SidebarMenuItem>
        <SidebarMenuButton size="lg" asChild>
          <Link to="/settings">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs">
                {user?.email ? getInitials(user.email) : 'AD'}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <div className="flex items-center gap-2">
                <span className="truncate font-semibold">
                  {user?.email?.split('@')[0] || 'Admin'}
                </span>
                {getRoleBadge()}
              </div>
              <span className="truncate text-xs text-muted-foreground">
                {user?.email || 'admin@example.com'}
              </span>
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {/* Logout Button - Always Visible */}
      <SidebarMenuItem>
        <SidebarMenuButton
          size="default"
          onClick={handleLogout}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" />
          <span>Log out</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar() {
  const { isSuperAdmin } = useAuthStore();
  const superAdmin = isSuperAdmin();

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  {superAdmin ? (
                    <Shield className="size-4" />
                  ) : (
                    <Building2 className="size-4" />
                  )}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Roni AI</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {superAdmin ? 'Platform Admin' : 'Admin Panel'}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {superAdmin ? (
          <>
            {/* Super Admin sees Platform Management */}
            <NavMain items={superAdminNavItems} label="Platform" />
          </>
        ) : (
          <>
            {/* Tenant users see Organization Management */}
            <NavMain items={tenantAdminNavItems} label="Management" />
            <SidebarSeparator />
            <NavMain items={contentNavItems} label="Content" />
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
